import './appInsights';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ChakraProvider } from '@chakra-ui/react';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import theme from './theme/theme';
import App from './App';
import './i18n';
import { appInsights } from './appInsights';

/**
 * Centralised error logging for React Query (mirrors the mobile app's
 * setup in apps/mobile/app/_layout.tsx). Every mutation / query failure
 * routes through App Insights with the queryKey / mutationKey as a
 * property so failures are searchable. Without this, per-hook onError
 * toasts dropped the actual error on the floor — admins saw a toast,
 * devs got no log, App Insights got no exception.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      const key = query.queryKey.map(String).join('.');
      console.warn(`[Query] ⚠ query failed [${key}]`, error);
      appInsights?.trackException(
        { exception: error as Error },
        { queryKey: key, kind: 'query' },
      );
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const key = mutation.options.mutationKey?.map(String).join('.') ?? '<no-key>';
      console.warn(`[Query] ⚠ mutation failed [${key}]`, error);
      appInsights?.trackException(
        { exception: error as Error },
        { mutationKey: key, kind: 'mutation' },
      );
    },
  }),
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ChakraProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ChakraProvider>
  </React.StrictMode>
);

